import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check, ChevronRight, Close, Note, OpenInNew, ContentCopy } from '@mui/icons-material';
import { Badge, Button, IconButton, Tooltip } from '@mui/material';

import { useCopyToClipboard } from '@hooks/useCopyToClipboard';

import { AppHeader } from '../components/app-header';
import { FilterableDataTable } from '../components/filterable-data-table';
import { LoadingState } from '../components/loading-state';
import { useGetProviderInfoQuery, useGetProviderNotesQuery } from '../store/api';
import { formatDateTime } from '../utils/formatting';

export default function NotesListPage() {
  const { providerId: urlProviderId } = useParams<{ providerId: string }>();
  const copy = useCopyToClipboard();

  // Handle URL encoding issues
  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;

  // State for pagination and filtering
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Memoized filter change handler
  const handleFiltersChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  }, []);

  // Memoized sort change handler
  const handleSortChange = useCallback((newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPage(1); // Reset to first page when sort changes
  }, []);

  // Fetch provider info
  const { data: provider, isLoading: userLoading } = useGetProviderInfoQuery(
    { providerId: providerId ?? '' },
    {
      skip: !providerId,
    },
  );

  const getStatusBadgeColor = (
    status: string | undefined | null,
  ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'active':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'error':
      case 'cancelled':
        return 'error';
      case 'draft':
      case 'inactive':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Get URL for each row
  const getRowUrl = useCallback((note: any) => {
    return `/admin-v2/note/${note.note_id}`;
  }, []);

  // Fetch provider's notes with pagination and filtering
  const { data: notesData, isLoading: notesLoading } = useGetProviderNotesQuery(
    {
      providerId: providerId ?? '',
      page,
      perPage,
      sortBy,
      sortOrder,
      ...filters,
    },
    {
      skip: !providerId,
    },
  );

  // Notes query is performed with pagination and filtering

  if (!providerId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <p className="text-gray-600 mt-2">No provider ID specified.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (userLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading provider details..." />
        </main>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const notesColumns = [
    {
      key: 'note_id',
      label: 'Note ID',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-600 break-all font-mono">{value}</span>
          <Tooltip title="Copy ID">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                copy(value, 'Note ID');
              }}
            >
              <ContentCopy fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'template',
      label: 'Template',
      render: (value: string) => <span className="text-sm text-gray-900">{value}</span>,
    },
    {
      key: 'patient_name',
      label: 'Patient',
      render: (value: string | null) => (
        <span className="text-sm text-gray-900">{value || 'N/A'}</span>
      ),
    },
    {
      key: 'encounter_type',
      label: 'Type',
      render: (value: string | null) => (
        <span className="text-sm text-gray-900">{value || 'N/A'}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <Badge color={getStatusBadgeColor(value)}>{value.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'has_feedback',
      label: 'Feedback',
      render: (value: boolean) =>
        value ? (
          <Check className="h-4 w-4 text-green-500" />
        ) : (
          <Close className="h-4 w-4 text-red-500" />
        ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatDateTime(value)}</span>
      ),
    },
  ];

  const filterConfigs = [
    {
      key: 'status',
      label: 'Status',
      type: 'select' as const,
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'completed', label: 'Completed' },
        { value: 'pending', label: 'Pending' },
        { value: 'failed', label: 'Failed' },
      ],
    },
    {
      key: 'template',
      label: 'Template',
      type: 'text' as const,
    },
    {
      key: 'encounterId',
      label: 'Encounter ID',
      type: 'text' as const,
    },
    {
      key: 'transcriptId',
      label: 'Transcript ID',
      type: 'text' as const,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/admin-v2">
                <Button
                  variant="text"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  <OpenInNew className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}>
                  <Button
                    variant="text"
                    className="text-sm font-medium text-gray-700 hover:text-blue-600"
                  >
                    {provider.name}
                  </Button>
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">Notes</span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Provider Header */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <Note className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-6">
                  <h1 className="text-2xl font-bold text-gray-900">Notes</h1>
                  <p className="text-gray-600">All notes for {provider.name}</p>
                  <p className="text-sm text-gray-500">Provider ID: {provider.provider_id}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Total Notes</div>
                <div className="text-2xl font-bold text-gray-900">{notesData?.total || 0}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filterable Data Table */}
        <FilterableDataTable
          title="Provider Notes"
          columns={notesColumns as any}
          data={notesData?.items || []}
          total={notesData?.total}
          loading={notesLoading}
          page={page}
          perPage={perPage}
          filters={filterConfigs}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          onFiltersChange={handleFiltersChange}
          onSortChange={handleSortChange}
          getRowUrl={getRowUrl}
          emptyMessage="No notes to display"
        />
      </main>
    </div>
  );
}
